import posthog from "posthog-js";
import type { AnalyticsAdapter } from "./types";

// PostHog API key (PaymentKit internal)
const POSTHOG_API_KEY = "phc_u9YDYUeUxo3CYn16XGrrwpVb8ycXgcjz5DarvIKRAAG";

/**
 * PostHog implementation of the analytics adapter.
 *
 * Singleton stored in window to ensure only one instance exists across
 * all script loads. Configures PostHog to use the reverse proxy at
 * /ingest to avoid ad blockers.
 */
export class PostHogAdapter implements AnalyticsAdapter {
  private initialized = false;

  /**
   * Get or create the PostHog adapter singleton (stored in window).
   *
   * @param apiBaseUrl - The API base URL for the reverse proxy
   * @throws Error if called with a different apiBaseUrl than the first call
   */
  static getInstance(apiBaseUrl: string): PostHogAdapter {
    if (typeof window === "undefined") {
      throw new Error("[PaymentKit Analytics] PostHog requires a browser environment");
    }

    const existing = window.__paymentKitPostHog__;
    if (existing) {
      if (existing.apiBaseUrl !== apiBaseUrl) {
        throw new Error(
          `[PaymentKit Analytics] PostHog already initialized with apiBaseUrl "${existing.apiBaseUrl}", ` +
            `cannot re-initialize with different apiBaseUrl "${apiBaseUrl}"`,
        );
      }
      return existing.instance;
    }

    const instance = new PostHogAdapter(apiBaseUrl);
    window.__paymentKitPostHog__ = { instance, apiBaseUrl };
    return instance;
  }

  private constructor(apiBaseUrl: string) {
    this.initPostHog(apiBaseUrl);
  }

  private initPostHog(apiBaseUrl: string): void {
    try {
      posthog.init(POSTHOG_API_KEY, {
        // Use reverse proxy to avoid ad blockers
        api_host: `${apiBaseUrl}/ingest`,
        // PostHog UI host (for toolbar, if ever needed)
        ui_host: "https://us.posthog.com",
        // Memory persistence - no cookies in embedded checkout iframe
        persistence: "memory",
        // Disable session recording - not needed for metrics
        disable_session_recording: true,
        // Manual events only - no autocapture
        autocapture: false,
        // Manual pageview control
        capture_pageview: false,
        // Disable batching - send events immediately to avoid data loss on page close
        request_batching: false,
        // Disable bot detection - allows events from automated tests and debugging environments
        // PostHog detects Playwright/headless browsers as bots by default
        opt_out_useragent_filter: true,
        // Callback when PostHog is ready
        loaded: () => {
          this.initialized = true;
        },
      });
    } catch (e) {
      console.warn("[PaymentKit Analytics] Failed to initialize PostHog:", e);
    }
  }

  identify(distinctId: string): void {
    try {
      posthog.identify(distinctId);
    } catch (e) {
      // Silent fail - never break checkout
      console.warn("[PaymentKit Analytics] Failed to identify:", e);
    }
  }

  capture(event: string, properties?: Record<string, unknown>): void {
    try {
      posthog.capture(event, properties);
    } catch (e) {
      // Silent fail - never break checkout
      console.warn("[PaymentKit Analytics] Failed to capture event:", e);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
